/**
 * Apoio da cotacao da corretora (P-NCT): os filtros da lista "Cotacoes" do OnPoint, os
 * passos do assistente e o que a tela repete do servidor so para desabilitar antes do
 * pedido. Quem decide e o servidor. Ver docs/ADR/0054-cotacao-da-corretora.md.
 */

/** Situacoes do filtro "Status da apolice" da corretora, na ordem do OnPoint (NCT-025). */
export const situacoesDaCorretora = [
  'cotacao',
  'em_analise',
  'aguardando_emissao_corretora',
  'aguardando_emissao_seguradora',
  'aguardando_pagamento_boleto',
  'emitida',
  'aguardando_corretora',
  'recusada',
  'cancelada',
  'rejeitada',
] as const;

/** Os filtros do dialogo "Cotacoes" da corretora (NCT-026 a NCT-037). */
export interface BrokerQuoteFilters {
  number: string;
  proposal_status: string;
  policyholder_name: string;
  policyholder_document: string;
  insured_name: string;
  insured_document: string;
  created_from: string;
  created_to: string;
  proposal_type: string;
  endorsement_type_id: string;
  /** "Tipo de cosseguro" (NCT-037): o tipo gravado no financeiro da cotacao. */
  coinsurance_type: string;
}

export const filtrosDaCorretoraVazios: BrokerQuoteFilters = {
  number: '',
  proposal_status: '',
  policyholder_name: '',
  policyholder_document: '',
  insured_name: '',
  insured_document: '',
  created_from: '',
  created_to: '',
  proposal_type: '',
  endorsement_type_id: '',
  coinsurance_type: '',
};

/**
 * Opcoes do "Tipo de cosseguro" do filtro: os valores gravados pelo financeiro da
 * cotacao (P-COT-4), os mesmos da apolice. As opcoes do OnPoint nao foram lidas.
 */
export const tiposDeCosseguro = [
  { value: 'cedido', label: 'Cedido' },
  { value: 'aceito', label: 'Aceito' },
];

/**
 * O numero de parcelas que a corretora digita: inteiro de 1 a 120, o limite de forma do
 * servidor. Quantas parcelas o tomador pode ter e regra pendente (PAR-001, PAR-247).
 */
export function avisoDoNumeroDeParcelas(texto: string): string | undefined {
  if (texto === '') return undefined;
  if (!/^\d+$/.test(texto)) return 'Informe um numero inteiro de parcelas.';
  const numero = Number(texto);
  if (numero < 1 || numero > 120) return 'O numero de parcelas vai de 1 a 120.';
  return undefined;
}

/** Quantos filtros estao aplicados. */
export function quantosFiltrosDaCorretora(filtros: BrokerQuoteFilters): number {
  return Object.values(filtros).filter((valor) => valor !== '').length;
}

/** O aviso do OnPoint quando a data final vem sem a inicial. */
export function avisoDoPeriodo(filtros: BrokerQuoteFilters): string | undefined {
  if (filtros.created_to && !filtros.created_from) {
    return 'Selecione tambem a data inicial para filtrar por periodo.';
  }
  return undefined;
}

/** Documento com 11 ou 14 digitos, como o OnPoint confere no filtro. */
export function avisoDoDocumento(documento: string): string | undefined {
  const digitos = documento.replace(/\D/g, '');
  if (digitos === '' || digitos.length === 11 || digitos.length === 14)
    return undefined;
  return 'Informe um CPF (11 digitos) ou CNPJ (14 digitos) valido.';
}

/** Os passos do assistente, na ordem do plano (secao 8.11 do 49). */
export const passosDoAssistente = [
  { key: 'tomador', label: 'Dados do tomador' },
  { key: 'segurado', label: 'Dados do segurado' },
  { key: 'risco', label: 'Dados do risco' },
  { key: 'objeto', label: 'Objeto da apolice' },
  { key: 'pagamento', label: 'Dados financeiros' },
  { key: 'resumo', label: 'Resumo da cotacao' },
] as const;

export type PassoDoAssistente = (typeof passosDoAssistente)[number]['key'];

export function passoValido(valor: string | undefined): PassoDoAssistente {
  return passosDoAssistente.find((passo) => passo.key === valor)?.key ?? 'tomador';
}

/** A cotacao so e gravada ao fim do passo do risco; os seguintes pedem a cotacao gravada. */
export function passoPedeCotacao(passo: PassoDoAssistente): boolean {
  return passo === 'objeto' || passo === 'pagamento' || passo === 'resumo';
}

/** Etapas em que a desistencia da corretora vale com `ate_emissao` (antes da emissao). */
const etapasQueDeclinam = [
  'em_cotacao',
  'aguardando_credito',
  'em_subscricao',
  'em_juridico',
  'aguardando_comercial',
  'minuta_gerada',
  'aguardando_ccg',
  'aguardando_assinatura',
];

/** Se a lista oferece "Cancelar cotacao" na linha, pela configuracao da empresa. */
export function podeCancelarNaLista(
  stage: string,
  escopo: 'cotacao' | 'ate_emissao' | undefined,
): boolean {
  if (stage === 'em_cotacao') return true;
  return escopo === 'ate_emissao' && etapasQueDeclinam.includes(stage);
}

/** Os grupos do "Historico da movimentacao" da corretora, na ordem do OnPoint (NCT-047). */
export const gruposDaMovimentacao = [
  'cotacao',
  'cadastro',
  'pep',
  'credito',
  'subscricao',
  'resseguro',
  'emissao',
] as const;
